import React, { useEffect, useRef, useState } from 'react';

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  /** Flip text + icon + hover bg to the error palette. Use for Delete-style actions. */
  isDestructive?: boolean;
  disabled?: boolean;
  separatorAbove?: boolean;
  'data-testid'?: string;
}

interface RenderTriggerArgs {
  open: boolean;
  toggle: () => void;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  children: (args: RenderTriggerArgs) => React.ReactNode;
  align?: 'left' | 'right';
  placement?: 'top' | 'bottom';
  width?: string;
  menuClassName?: string;
  'data-testid'?: string;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  children,
  align = 'right',
  placement = 'bottom',
  width = 'w-44',
  menuClassName,
  'data-testid': testId,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const toggle = () => setOpen((v) => !v);

  const handleClick = (item: ActionMenuItem) => {
    if (item.disabled) return;
    setOpen(false);
    item.onClick();
  };

  const alignClass = align === 'right' ? 'right-0' : 'left-0';
  const placementClass =
    placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';

  return (
    <div ref={ref} className="relative">
      {children({ open, toggle })}
      {open && (
        <div
          role="menu"
          className={`absolute ${alignClass} ${placementClass} ${width} rounded-xl bg-surface-elevated border border-border-primary/20 shadow-lg overflow-hidden z-50 ${menuClassName ?? ''}`}
          data-testid={testId}
        >
          {items.map((item, idx) => {
            // Subtle hover tint — primary for normal items, error for destructive ones.
            // Icons live in their own color span so they pop without needing per-icon props.
            const itemColors = item.isDestructive
              ? 'text-error-600 hover:bg-error-500/10'
              : 'text-text-primary hover:bg-primary-500/10 dark:hover:bg-primary-500/15';
            const iconColor = item.isDestructive ? 'text-error-500' : 'text-primary-500';
            const disabledClass = item.disabled
              ? 'opacity-50 cursor-not-allowed'
              : '';

            return (
              <React.Fragment key={`${item.label}-${idx}`}>
                {item.separatorAbove && (
                  <div className="border-t border-border-primary/20" />
                )}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => handleClick(item)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium transition-colors ${itemColors} ${disabledClass}`}
                  data-testid={item['data-testid']}
                >
                  {item.icon && (
                    <span className={`shrink-0 flex ${iconColor}`}>{item.icon}</span>
                  )}
                  {item.label}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionMenu;
